/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import { useState, useMemo } from "react";
import PropTypes from "prop-types";

// @mui material components
import {
  DataGrid,
} from '@mui/x-data-grid';

// Material Dashboard React components
import MDBox from "components/MDBox";

import CustomToolbar from "./toolbar";

// --- HELPER: Extract plain text from possible JSX/React Elements ---
const getTextFromValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);

  if (typeof value === "object") {
    // If it's a React Element with children (e.g., <span ...>Text</span>)
    if (value.props && value.props.children) {
      return getTextFromValue(value.props.children);
    }
    // If it's an array (e.g., fragments)
    if (Array.isArray(value)) {
      return value.map(getTextFromValue).join(" ");
    }
  }
  return String(value);
};

// --- HELPER: LocalStorage Management (Added) ---
const saveTableState = (tableId, key, value) => {
  if (!tableId) return;
  try {
    const existing = JSON.parse(localStorage.getItem(tableId)) || {};
    const updated = { ...existing, [key]: value };
    localStorage.setItem(tableId, JSON.stringify(updated));
  } catch (e) {
    console.error("Failed to save table state", e);
  }
};

const loadTableState = (tableId) => {
  if (!tableId) return {};
  try {
    return JSON.parse(localStorage.getItem(tableId)) || {};
  } catch (e) {
    return {};
  }
};

function DataTable({
  entriesPerPage,
  canSearch,
  hasFilters,
  openFiltersModal,
  table,
  isSorted,
  isLoading,
  noTablePadding,
  noToolbar,
  noHeaders,
  tableId // <--- Added prop for persistence
}) {
  const safeEntries = entriesPerPage || { defaultValue: 10, entries: [5, 10, 15, 20, 25] };
  const pageSizeDefault = safeEntries.defaultValue || 10;
  const pageSizeOptions = safeEntries.entries || [5, 10, 15, 20, 25];
  const [searchText, setSearchText] = useState("");

  // ---------------------------------------------------------------------------
  // 1. LOAD SAVED STATE ON MOUNT (Added)
  // ---------------------------------------------------------------------------
  const initialState = useMemo(() => {
    const saved = loadTableState(tableId);
    return {
      pagination: {
        paginationModel: { pageSize: pageSizeDefault, page: 0 },
      },
      sorting: saved.sorting || undefined,
      columns: saved.columns || undefined,
      // FIX: Ensure we extract the 'value' string, not the whole object
      density: saved.density || { value: 'standard' }
    };
  }, [tableId, pageSizeDefault]);

  // ---------------------------------------------------------------------------
  // 3. COLUMN ADAPTER (Legacy -> MUI DataGrid)
  // ---------------------------------------------------------------------------
  const columns = useMemo(() => {
    return table.columns.map((col) => {
      let flexValue;
      let widthValue;
      let minWidthValue = 120; // Default safety for text columns

      if (typeof col.width === 'string' && col.width.includes('%')) {
        const val = parseFloat(col.width);
        if (!isNaN(val)) flexValue = val;
      } else if (typeof col.width === 'number') {
        widthValue = col.width;
        minWidthValue = col.width;
        flexValue = 0;
      } else {
        flexValue = 1;
      }

      return {
        field: col.accessor,
        headerName: col.Header,
        flex: flexValue,
        width: widthValue,
        minWidth: minWidthValue,
        sortable: isSorted && !col.disableSortBy,
        type: col.type,                   
        valueOptions: col.valueOptions,   
        headerAlign: 'left',
        
        // IMPORTANT: Tells DataGrid how to get the "Clean Value" for filtering/sorting
        valueGetter: (params, row) => {
          const rowData = row || (params && params.row);
          if (!rowData || !col.accessor) return "";
          const rawValue = rowData[col.accessor];

          // CRITICAL FIX: If it is a number column, return the raw Number.
          // Do NOT convert it to a string, or it will sort incorrectly (1, 10, 2).
          if (col.type === 'number') {
            return Number(rawValue);
          }

          // Otherwise, use your helper for text/JSX
          return getTextFromValue(rawValue);
        },

        // Render Cell Adapter
        renderCell: (params) => {
          const originalValue = params.row[col.accessor];

          if (col.Cell) {
            const rowObject = { original: params.row };
            const cellObject = { value: originalValue, row: rowObject };
            const infoMock = { cell: cellObject, row: rowObject, value: originalValue };

            return (
              <MDBox
                width="100%"
                display="flex"
                alignItems="center"
                sx={{ lineHeight: 1.5, whiteSpace: 'normal' }}
              >
                {col.Cell(infoMock)}
              </MDBox>
            );
          }

          return (
            <div style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: '1.5',
              padding: '10px 0',
              width: '100%'
            }}>
              {originalValue}
            </div>
          );
        },
      };
    });
  }, [table.columns, isSorted]);

  // ---------------------------------------------------------------------------
  // 4. CLIENT-SIDE SEARCH LOGIC
  // ---------------------------------------------------------------------------
  const rows = useMemo(() => {
    if (!searchText) return table.rows;
    const lowerSearch = searchText.toLowerCase();

    return table.rows.filter((row) => {
      return Object.values(row).some((val) => {
        const textValue = getTextFromValue(val);
        return textValue && textValue.toLowerCase().includes(lowerSearch);
      });
    });
  }, [table.rows, searchText]);

  // Return the DataTable component
  return (
    <MDBox
      sx={{
        backgroundColor: "white",
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      {/* REMOVED THE OLD TOP CONTROL BAR MDBox HERE */}

      {/* --- DATA GRID --- */}
      <MDBox
        width="100%"
        pl={noTablePadding ? 0 : 3}
        pr={noTablePadding ? 0 : 3}
        pb={noTablePadding ? 0 : 3}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id || Math.random()}
          columnHeaderHeight={noHeaders ? 0 : 'auto'}
          pagination
          pageSizeOptions={pageSizeOptions}
          fullWidth

          // --- APPLIED INITIAL STATE & PERSISTENCE LISTENERS (Added) ---
          initialState={initialState}

          onSortModelChange={(model) => {
            saveTableState(tableId, 'sorting', { sortModel: model });
          }}
          onColumnVisibilityModelChange={(model) => {
            saveTableState(tableId, 'columns', { columnVisibilityModel: model });
          }}

          // --- IMPLEMENTING CUSTOM TOOLBAR ---
          slots={noToolbar ? {} : {
            // Use our custom component instead of the default GridToolbar
            toolbar: CustomToolbar
          }}

          // Pass props to the custom toolbar
          slotProps={{
            toolbar: {
              setSearchText,
              searchText,
              canSearch,
              hasFilters,
              openFiltersModal,
              onDensityChange: (density) => {
                saveTableState(tableId, 'density', { value: density });
              }
            }
          }}

          disableRowSelectionOnClick
          sx={{
            border: 0,
            // --- HEADER STYLING APPLIED HERE ---
            '& .MuiDataGrid-columnHeader': {
              color: '#344767', // Dark Blue/Grey from your theme
              fontWeight: 900,  // Bold font
            },
            '& .MuiDataGrid-columnHeaderTitle': {
            }
          }} />
      </MDBox>
    </MDBox>
  );
}
// ---------------------------------------------------------------------------
// PROPTYPES & DEFAULTS
// ---------------------------------------------------------------------------
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  hasFilters: false,
  showTotalEntries: true,
  showEntriesPerPage: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  isLoading: false,
  noEndBorder: false,
  noTablePadding: false,
  noHeaders: false,
  noToolbar: false,
  tableId: null, // Default to null (persistence disabled)
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  hasFilters: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  showEntriesPerPage: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary", "secondary", "info", "success", "warning", "error", "dark", "light",
    ]),
  }),
  openFiltersModal: PropTypes.func,
  isSorted: PropTypes.bool,
  isLoading: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  noTablePadding: PropTypes.bool,
  noHeaders: PropTypes.bool,
  noToolbar: PropTypes.bool,
  tableId: PropTypes.string, // Added PropType
};

export default DataTable;